import { useRef, useState } from 'react';
import { Plus, Router as RouterIcon, HardDrive, Camera as CameraIcon, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import NewRouterCard from './NewRouterCard';
import NewNVRCard from './NewNVRCard';
import NewMicrophoneCard from './NewMicrophoneCard';
import { UnifiedCameraDialog } from './UnifiedCameraDialog';

type Kind = 'router' | 'nvr' | 'camera' | 'microphone';

interface Props {
  zoneId: number | string;
  /** Called after a successful create. Parent should re-fetch its lists. */
  onCreated: () => void;
}

/**
 * Zone-level "+ Add device ▾" affordance.
 *
 * Renders a single button that opens a popover with three options
 * (Router, NVR, Camera). Picking Router or NVR mounts the matching
 * inline new-card form below the button (one at a time). Picking
 * Camera opens the existing modal `<UnifiedCameraDialog>` — keeps the
 * camera-create flow on its proven path.
 *
 * Designed per 2026-05-17 spec §2.1 + §2.3. The discover-from-jetson
 * affordances inside each new card are deferred until task #8
 * (`useDiscover()` + `<DiscoverButton>`) lands.
 */
export default function AddDeviceDropdown({ zoneId, onCreated }: Props) {
  const { t } = useT();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openKind, setOpenKind] = useState<Kind | null>(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);

  const pick = (kind: Kind) => {
    setMenuOpen(false);
    if (kind === 'camera') {
      setOpenKind(null);
      setCameraDialogOpen(true);
      return;
    }
    setOpenKind(kind);
  };

  const closeOpen = () => setOpenKind(null);
  const handleCreated = () => {
    setOpenKind(null);
    onCreated();
  };

  return (
    <div className="space-y-3">
      <div>
        <Button
          ref={buttonRef}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Plus className="size-4" />
          {t('equipment.addDevice.button')}
        </Button>
        <Popover
          open={menuOpen}
          onOpenChange={setMenuOpen}
          triggerRef={buttonRef}
          side="bottom"
          align="start"
        >
          <div className="flex flex-col py-1 min-w-[10rem]" role="menu">
            <MenuItem onClick={() => pick('router')} icon={<RouterIcon className="size-4" />}>
              {t('equipment.addDevice.router')}
            </MenuItem>
            <MenuItem onClick={() => pick('nvr')} icon={<HardDrive className="size-4" />}>
              {t('equipment.addDevice.nvr')}
            </MenuItem>
            <MenuItem onClick={() => pick('camera')} icon={<CameraIcon className="size-4" />}>
              {t('equipment.addDevice.camera')}
            </MenuItem>
            <MenuItem onClick={() => pick('microphone')} icon={<Mic className="size-4" />}>
              {t('equipment.addDevice.microphone')}
            </MenuItem>
          </div>
        </Popover>
      </div>

      {openKind === 'router' && (
        <NewRouterCard zoneId={zoneId} onCreated={handleCreated} onCancel={closeOpen} />
      )}
      {openKind === 'nvr' && (
        <NewNVRCard zoneId={zoneId} onCreated={handleCreated} onCancel={closeOpen} />
      )}
      {openKind === 'microphone' && (
        <NewMicrophoneCard zoneId={zoneId} onCreated={handleCreated} onCancel={closeOpen} />
      )}

      <UnifiedCameraDialog
        open={cameraDialogOpen}
        onOpenChange={setCameraDialogOpen}
        onSaved={onCreated}
        zoneId={String(zoneId)}
        camera={null}
        provider={null}
      />
    </div>
  );
}

function MenuItem({
  onClick,
  icon,
  children,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
        'hover:bg-muted hover:text-foreground',
      )}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
